import { computed } from 'vue'

const TEAM_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#06b6d4',
  '#f59e0b',
  '#ef4444',
  '#10b981',
  '#f97316',
  '#84cc16',
  '#e11d48',
  '#14b8a6'
]

const EMPLOYEE_COLORS = [
  '#60a5fa',
  '#a78bfa',
  '#22d3ee',
  '#fbbf24',
  '#f87171',
  '#34d399',
  '#fb923c',
  '#a3e635',
  '#fb7185',
  '#2dd4bf',
  '#93c5fd',
  '#c4b5fd',
  '#67e8f9',
  '#fcd34d'
]

const DERDEN_COLOR = '#60a5fa'
const DERDEN_NTF_COLOR = '#a78bfa'
const DERDEN_DRILLDOWN_COLORS = [
  '#c084fc',
  '#a78bfa',
  '#818cf8',
  '#7dd3fc',
  '#67e8f9',
  '#5eead4',
  '#86efac',
  '#fde047'
]

const DERDEN_SERIES_NAME = 'Derden Prognose'
const DERDEN_NTF_SERIES_NAME = 'Derden Prognose NTF'
const ACTUAL_DERDEN_SERIES = 'Derden realisatie'
const LEADS_VERKENNINGEN_SERIES = 'Leads'
const PROSPECTS_SERIES = 'Prospects'
const SOURCING_SERIES = 'Sourcing'
const BUDGET_HOURS_SERIES = 'Begroting Uren'
const BUDGET_DERDEN_SERIES = 'Begroting Derden'

const PIPELINE_DRILLDOWN_COLORS = [
  '#fbbf24',
  '#f59e0b',
  '#d97706',
  '#f97316',
  '#ea580c',
  '#fb923c',
  '#fdba74',
  '#fcd34d',
  '#fde68a',
  '#fed7aa'
]

const BUDGET_SERIES_NAMES = [BUDGET_HOURS_SERIES, BUDGET_DERDEN_SERIES]

const LEGEND_DESCRIPTIONS = {
  Uren: "De urenomzet uit de timesheets van de te sourcen collega's (dus excl. Directie en Webdevelopers).",
  'Derden realisatie':
    'Gefactureerde derdenomzet zoals berekend en gerapporteerd na elke maandafsluiting.',
  Sourcing: 'De omzetinschatting van geconcretiseerde sourcingskansen.',
  Prospects: 'Omzetverwachting (waarde × kans) van prospects uit de Salesfunnel in Simplicate.',
  Leads:
    'Omzetverwachting (waarde × kans) van Leads en Verkenningen uit de Salesfunnel in Simplicate.',
  'Derden Prognose':
    'Verwachte omzet van de nog te factureren vaste-prijs diensten. Gebaseerd op verkoopprijs en datum te factureren vanaf.',
  'Derden Prognose NTF': 'De Derden Prognose, maar van reeds afgesloten maanden.',
  'Begroting Uren':
    'Totale urenomzet uit de begroting, hier dus inclusief omzet van Directie en Webdevelopers.',
  'Begroting Derden': 'Totale derdenomzet uit de begroting.'
}

/**
 * Composable for building ECharts option configurations for the HoursRevenueChart.
 *
 * @param {Object} params
 * @param {import('vue').Ref<Object>} params.data - Revenue data with months, teams, totals
 * @param {import('vue').Ref<Object|null>} params.derdenOmzetData - Third-party revenue data
 * @param {import('vue').Ref<Array>} params.budgetData - Budget data array
 * @param {import('vue').Ref<Object|null>} params.pipelineData - Pipeline revenue data
 * @param {import('vue').Ref<string>} params.drilldownLevel - Current drilldown level
 * @param {import('vue').Ref<Object|null>} params.drilldownTeam - Selected team for employee drilldown
 * @param {import('vue').Ref<string|null>} params.drilldownPipelineCategory - Selected pipeline category
 * @param {import('vue').Ref<boolean>} params.isDarkMode - Whether dark mode is active
 */
export function useHoursRevenueChartConfig({
  data,
  derdenOmzetData,
  budgetData,
  pipelineData,
  drilldownLevel,
  drilldownTeam,
  drilldownPipelineCategory,
  isDarkMode
}) {
  function formatCurrency(amount) {
    if (!amount) return '\u20AC 0'
    return `\u20AC ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
  }

  function formatMonthLabel(monthKey) {
    const [year, month] = monthKey.split('-')
    const monthNames = [
      'jan',
      'feb',
      'mrt',
      'apr',
      'mei',
      'jun',
      'jul',
      'aug',
      'sep',
      'okt',
      'nov',
      'dec'
    ]
    return `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`
  }

  function buildTooltipFormatter() {
    return params => {
      if (!params?.length) return ''
      const monthName = params[0].axisValue
      const actualParams = params.filter(p => !BUDGET_SERIES_NAMES.includes(p.seriesName))
      const budgetParams = params.filter(p => BUDGET_SERIES_NAMES.includes(p.seriesName))
      if (drilldownLevel.value === 'pipeline') {
        actualParams.sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
      }
      const totalRevenue = actualParams.reduce((s, p) => s + (Number(p.value) || 0), 0)
      let html = `<div style="font-weight:600;margin-bottom:6px;">${monthName}</div>`
      for (const p of actualParams) {
        if (!p.value) continue
        const pct = totalRevenue > 0 ? Math.round((p.value / totalRevenue) * 100) : 0
        html += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;">
        <span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:2px;"></span>
        <span>${p.seriesName}</span>
        <span style="margin-left:auto;font-weight:500;">${formatCurrency(p.value)} (${pct}%)</span>
      </div>`
      }
      html += `<div style="margin-top:3px;font-weight:600;">Totaal: ${formatCurrency(totalRevenue)}</div>`
      if (budgetParams.some(p => p.value)) {
        const budgetTotal = budgetParams.reduce((s, p) => s + (Number(p.value) || 0), 0)
        html += `<div style="margin-top:8px;margin-bottom:4px;font-weight:600;opacity:0.8;font-style:italic;">Begroting</div>`
        for (const p of budgetParams) {
          if (!p.value) continue
          html += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;opacity:0.8;">
          <span style="display:inline-block;width:10px;height:2px;background:${p.color};border-radius:1px;"></span>
          <span style="font-style:italic;">${p.seriesName}</span>
          <span style="margin-left:auto;font-weight:500;">${formatCurrency(p.value)}</span>
        </div>`
        }
        if (budgetTotal > 0) {
          html += `<div style="margin-top:3px;font-weight:600;opacity:0.8;font-style:italic;">Totaal: ${formatCurrency(budgetTotal)}</div>`
        }
      }
      return html
    }
  }

  function buildBaseOptions(colors, monthLabels) {
    return {
      animation: false,
      grid: { left: 80, right: 30, top: 50, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        appendToBody: true,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        textStyle: { color: colors.textPrimary, fontSize: 12 },
        extraCssText: 'z-index: 9999;',
        position: (point, params, dom, rect, size) => {
          const chartRect = rect || { x: 0, y: 0 }
          const viewportX = chartRect.x + point[0]
          const viewportY = chartRect.y + point[1]
          const x = viewportX + 15
          const y = Math.max(10, viewportY - size.contentSize[1] / 2)
          const maxX = window.innerWidth - size.contentSize[0] - 20
          return [Math.min(x, maxX), y]
        },
        formatter: buildTooltipFormatter()
      },
      legend: {
        top: 0,
        type: 'scroll',
        textStyle: { color: colors.textPrimary, fontSize: 12 },
        pageTextStyle: { color: colors.textSecondary },
        pageIconColor: colors.textSecondary,
        pageIconInactiveColor: colors.border,
        tooltip: {
          show: drilldownLevel.value === 'overview',
          confine: true,
          position: 'bottom',
          backgroundColor: colors.tooltipBg,
          borderColor: colors.tooltipBorder,
          textStyle: { color: colors.textPrimary, fontSize: 12 },
          extraCssText: 'text-align:left;white-space:pre-line;max-width:280px;',
          formatter: params => LEGEND_DESCRIPTIONS[params.name] || params.name
        }
      },
      xAxis: {
        type: 'category',
        data: monthLabels,
        axisLabel: { color: colors.textPrimary, fontSize: 12 },
        axisLine: { lineStyle: { color: colors.border } },
        axisTick: { alignWithLabel: true, lineStyle: { color: colors.border } }
      },
      yAxis: {
        type: 'value',
        name: 'Omzet',
        nameTextStyle: { fontSize: 11, color: colors.textSecondary },
        axisLabel: {
          color: colors.textSecondary,
          fontSize: 11,
          formatter: v => {
            if (v >= 1000000) return `\u20AC${(v / 1000000).toFixed(1)}M`
            if (v >= 1000) return `\u20AC${(v / 1000).toFixed(0)}K`
            return `\u20AC${v}`
          }
        },
        axisLine: { lineStyle: { color: colors.border } },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      }
    }
  }

  function buildBudgetMap() {
    const map = {}
    for (const b of budgetData.value) {
      const key = `${b.year}-${String(b.month).padStart(2, '0')}`
      map[key] = b
    }
    return map
  }

  function buildOverviewChart(colors) {
    const months = data.value.months
    const monthLabels = months.map(formatMonthLabel)

    const series = [
      {
        name: 'Uren',
        type: 'bar',
        stack: 'total',
        data: months.map(m => data.value.totals?.months[m]?.billable_revenue || 0),
        itemStyle: { color: '#15803d' },
        emphasis: { focus: 'series' }
      }
    ]

    // Behaalde derden omzet bar (between Uren and Derden omzet)
    if (budgetData.value?.length) {
      const budgetMapForActual = buildBudgetMap()
      const hasActualDerden = months.some(m => {
        const val = parseFloat(budgetMapForActual[m]?.actual_derden_omzet) || 0
        return val > 0
      })
      if (hasActualDerden) {
        series.push({
          name: ACTUAL_DERDEN_SERIES,
          type: 'bar',
          stack: 'total',
          data: months.map(m => {
            const val = parseFloat(budgetMapForActual[m]?.actual_derden_omzet) || 0
            return val || 0
          }),
          itemStyle: { color: '#1d4ed8' },
          emphasis: { focus: 'series' }
        })
      }
    }

    // Pipeline series (between actual derden and derden omzet)
    if (pipelineData.value) {
      const sr = pipelineData.value.sourcing || {}
      const hasSourcing = months.some(m => (sr[m] || 0) > 0)
      if (hasSourcing) {
        series.push({
          name: SOURCING_SERIES,
          type: 'bar',
          stack: 'total',
          data: months.map(m => Math.round(sr[m] || 0)),
          itemStyle: { color: '#86efac' },
          emphasis: { focus: 'series' }
        })
      }

      const pr = pipelineData.value.prospects || {}
      const hasProspects = months.some(m => (pr[m] || 0) > 0)
      if (hasProspects) {
        series.push({
          name: PROSPECTS_SERIES,
          type: 'bar',
          stack: 'total',
          data: months.map(m => Math.round(pr[m] || 0)),
          itemStyle: { color: '#f97316' },
          emphasis: { focus: 'series' }
        })
      }

      const lv = pipelineData.value.leads_verkenningen || {}
      const hasLeads = months.some(m => (lv[m] || 0) > 0)
      if (hasLeads) {
        series.push({
          name: LEADS_VERKENNINGEN_SERIES,
          type: 'bar',
          stack: 'total',
          data: months.map(m => Math.round(lv[m] || 0)),
          itemStyle: { color: '#fbbf24' },
          emphasis: { focus: 'series' }
        })
      }
    }

    if (derdenOmzetData.value?.months?.length) {
      const budgetMapForMasking = buildBudgetMap()
      const totalsMonths = derdenOmzetData.value.totals?.months || {}
      const totalsUitloop = derdenOmzetData.value.totals?.uitloop || {}

      series.push({
        name: DERDEN_SERIES_NAME,
        type: 'bar',
        stack: 'total',
        data: months.map(m => {
          const actual = parseFloat(budgetMapForMasking[m]?.actual_derden_omzet) || 0
          if (actual > 0) return 0
          const total = totalsMonths[m]?.derden_omzet || 0
          const ntf = totalsUitloop[m] || 0
          return Math.max(0, total - ntf)
        }),
        itemStyle: {
          color: DERDEN_COLOR,
          decal: {
            symbol: 'rect',
            dashArrayX: [1, 0],
            dashArrayY: [4, 3],
            rotation: 0.8
          }
        },
        emphasis: { focus: 'series' }
      })

      if (derdenOmzetData.value.uitloopApplicable) {
        series.push({
          name: DERDEN_NTF_SERIES_NAME,
          type: 'bar',
          stack: 'total',
          data: months.map(m => {
            const actual = parseFloat(budgetMapForMasking[m]?.actual_derden_omzet) || 0
            if (actual > 0) return 0
            return totalsUitloop[m] || 0
          }),
          itemStyle: {
            color: DERDEN_NTF_COLOR,
            decal: {
              symbol: 'rect',
              dashArrayX: [1, 0],
              dashArrayY: [4, 3],
              rotation: -0.8
            }
          },
          emphasis: { focus: 'series' }
        })
      }
    }

    // Budget lines (only in overview)
    if (budgetData.value?.length) {
      const budgetMap = buildBudgetMap()
      const hasHoursBudget = months.some(m => budgetMap[m]?.budget_hours_revenue > 0)
      const hasDerdenBudget = months.some(m => budgetMap[m]?.budget_derden_omzet > 0)

      if (hasHoursBudget) {
        series.push({
          name: BUDGET_HOURS_SERIES,
          type: 'line',
          stack: 'budget',
          data: months.map(m => {
            const val = parseFloat(budgetMap[m]?.budget_hours_revenue) || 0
            return val || null
          }),
          symbol: 'none',
          smooth: true,
          lineStyle: { color: '#15803d', width: 2, type: 'dashed' },
          itemStyle: { color: '#15803d' },
          z: 10
        })
      }

      if (hasDerdenBudget) {
        series.push({
          name: BUDGET_DERDEN_SERIES,
          type: 'line',
          stack: 'budget',
          data: months.map(m => {
            const val = parseFloat(budgetMap[m]?.budget_derden_omzet) || 0
            return val || null
          }),
          symbol: 'none',
          smooth: true,
          lineStyle: { color: '#1d4ed8', width: 2, type: 'dashed' },
          itemStyle: { color: '#1d4ed8' },
          z: 10
        })
      }
    }

    return { ...buildBaseOptions(colors, monthLabels), series }
  }

  function buildTeamChart(colors) {
    const months = data.value.months
    const monthLabels = months.map(formatMonthLabel)
    const teams = data.value.teams

    const series = teams.map((team, i) => ({
      name: team.team_name,
      type: 'bar',
      stack: 'total',
      data: months.map(m => team.subtotals.months[m]?.billable_revenue || 0),
      itemStyle: { color: TEAM_COLORS[i % TEAM_COLORS.length] },
      emphasis: { focus: 'series' }
    }))

    return { ...buildBaseOptions(colors, monthLabels), series }
  }

  function buildEmployeeChart(team, colors) {
    const months = data.value.months
    const monthLabels = months.map(formatMonthLabel)
    const employees = team.employees

    const series = employees.map((emp, i) => ({
      name: emp.employee_name,
      type: 'bar',
      stack: 'total',
      data: months.map(m => emp.months[m]?.billable_revenue || 0),
      itemStyle: { color: EMPLOYEE_COLORS[i % EMPLOYEE_COLORS.length] },
      emphasis: { focus: 'series' }
    }))

    return { ...buildBaseOptions(colors, monthLabels), series }
  }

  function buildDerdenDrilldownChart(colors) {
    if (!derdenOmzetData.value) return buildBaseOptions(colors, [])

    const months = data.value.months
    const monthLabels = months.map(formatMonthLabel)
    const budgetMapForMasking = buildBudgetMap()
    const isMasked = m => (parseFloat(budgetMapForMasking[m]?.actual_derden_omzet) || 0) > 0

    const groups = derdenOmzetData.value.revenue_groups.filter(group =>
      months.some(m => !isMasked(m) && group.months[m]?.derden_omzet)
    )

    const series = groups.map((group, i) => ({
      name: group.group_name,
      type: 'bar',
      stack: 'total',
      data: months.map(m => (isMasked(m) ? 0 : group.months[m]?.derden_omzet || 0)),
      itemStyle: { color: DERDEN_DRILLDOWN_COLORS[i % DERDEN_DRILLDOWN_COLORS.length] },
      emphasis: { focus: 'series' }
    }))

    return { ...buildBaseOptions(colors, monthLabels), series }
  }

  function buildPipelineDrilldownChart(colors) {
    const months = data.value.months
    const monthLabels = months.map(formatMonthLabel)
    const category = drilldownPipelineCategory.value
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    const sumMonths = kans => Object.values(kans.months).reduce((s, v) => s + v, 0)
    const sortKey = months.includes(currentMonth)
      ? kans => Number(kans.months[currentMonth] || 0)
      : sumMonths
    const details = (pipelineData.value?.details || [])
      .filter(d => d.category === category)
      .sort((a, b) => sortKey(b) - sortKey(a))

    const series = details.map((kans, i) => ({
      name: kans.name,
      type: 'bar',
      stack: 'total',
      data: months.map(m => Math.round(kans.months[m] || 0)),
      itemStyle: { color: PIPELINE_DRILLDOWN_COLORS[i % PIPELINE_DRILLDOWN_COLORS.length] },
      emphasis: { focus: 'series' }
    }))

    return { ...buildBaseOptions(colors, monthLabels), series }
  }

  const chartOption = computed(() => {
    const dark = isDarkMode.value
    const colors = {
      textPrimary: dark ? '#e5e7eb' : '#374151',
      textSecondary: dark ? '#9ca3af' : '#6b7280',
      border: dark ? '#4b5563' : '#e5e7eb',
      tooltipBg: dark ? '#1f2937' : '#ffffff',
      tooltipBorder: dark ? '#374151' : '#e5e7eb'
    }

    switch (drilldownLevel.value) {
      case 'pipeline':
        return buildPipelineDrilldownChart(colors)
      case 'derden':
        return buildDerdenDrilldownChart(colors)
      case 'employees':
        return buildEmployeeChart(drilldownTeam.value, colors)
      case 'teams':
        return buildTeamChart(colors)
      default:
        return buildOverviewChart(colors)
    }
  })

  function handleClick(params) {
    if (params.componentType !== 'series') return
    if (BUDGET_SERIES_NAMES.includes(params.seriesName)) return
    if (params.seriesName === ACTUAL_DERDEN_SERIES) return

    if (drilldownLevel.value === 'overview') {
      if (params.seriesName === LEADS_VERKENNINGEN_SERIES) {
        drilldownPipelineCategory.value = 'leads_verkenningen'
        drilldownLevel.value = 'pipeline'
      } else if (params.seriesName === PROSPECTS_SERIES) {
        drilldownPipelineCategory.value = 'prospects'
        drilldownLevel.value = 'pipeline'
      } else if (params.seriesName === SOURCING_SERIES) {
        drilldownPipelineCategory.value = 'sourcing'
        drilldownLevel.value = 'pipeline'
      } else if (
        params.seriesName === DERDEN_SERIES_NAME ||
        params.seriesName === DERDEN_NTF_SERIES_NAME
      ) {
        if (derdenOmzetData.value?.revenue_groups?.length) {
          drilldownLevel.value = 'derden'
        }
      } else if (params.seriesName === 'Uren') {
        drilldownLevel.value = 'teams'
      }
      return
    }

    if (drilldownLevel.value === 'teams') {
      const team = data.value.teams.find(t => t.team_name === params.seriesName)
      if (team) {
        drilldownTeam.value = team
        drilldownLevel.value = 'employees'
      }
    }
  }

  return {
    chartOption,
    handleClick
  }
}
